import { useCallback } from 'react';

import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';

/**
 * Shared rename flow for uploaded documents: writes the display label, refetches
 * so the new name shows, and surfaces its own error/success toasts. Rethrows on
 * failure so callers can keep their edit UI open for a retry.
 *
 * Used by the Internal Documents table (RenameDocumentModal) and the image
 * preview modal (DocumentPreviewCard) so the two flows can't drift apart.
 */
export function useRenameDocument({ accountId, getDocName, fetchDocuments }) {
  const toast = useToast();
  const {
    account: {
      policies: { setDocumentLabel }
    }
  } = useStore();

  return useCallback(
    async (path, label) => {
      try {
        await setDocumentLabel(accountId, path, label);
      } catch (error) {
        toast.notify({ type: 'error', message: `Couldn't rename ${getDocName(path)}` });
        throw error;
      }
      // Re-fetch separately so a fetch failure isn't misreported as a rename
      // failure. The rename already committed, so swallow a fetch error rather
      // than rethrow — otherwise the modal stays open with no toast and the user
      // can't tell their rename actually worked. The list catches up on next nav.
      try {
        await fetchDocuments();
      } catch {
        // Intentionally ignored — rename succeeded; the stale list is cosmetic.
      }
      toast.notify({ type: 'success', message: 'Document renamed successfully' });
    },
    [accountId, setDocumentLabel, getDocName, fetchDocuments, toast]
  );
}
